/**
 * <video-component> — video.js wrapper supporting html5 / youtube / vimeo,
 * lazy-load via <template> hydration, IntersectionObserver autoplay-in-view,
 * and integration with swiper slide-changed events.
 *
 * Loaded ONLY from sections/snippets that render `<video-component>` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/responsive
 *   - @theme/analytics (video_start, GA4-EVENTS-PLAN.md section 5.3)
 *   - window.videojs (loaded dynamically via window.loadScript)
 *   - window.loadScript (still in global.js)
 *   - window.theme.videoScript (theme.liquid inline config)
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { EVENTS, MAX_PARAM_LENGTH, trackEvent } from '@theme/analytics';

const VIDEOJS_LOAD_MAX_ATTEMPTS = 50;
const VIDEOJS_LOAD_RETRY_MS = 100;

// One video_start per video per page. The same media id lives in several players at
// once (PDP mobile swiper + desktop stack, the lightbox clone) and a player is re-created
// on section re-render; the lookbook tab swap changes the pathname without a page load,
// which is what makes it a new page here.
const reportedVideoStarts = new Set();

// GA4 cuts parameter values at 100 characters (analytics.js does the same) and Shopify's
// product-video URLs run to ~125, so those fall back to the path, then to the file name.
function reportableUrl(url) {
  if (url.length <= MAX_PARAM_LENGTH) return url;
  try {
    const { pathname } = new URL(url, window.location.href);
    return pathname.length <= MAX_PARAM_LENGTH ? pathname : pathname.split('/').pop();
  } catch {
    return url.slice(0, MAX_PARAM_LENGTH);
  }
}

function videoTitleFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.searchParams.get('v') || parsed.pathname.split('/').pop() || url;
  } catch {
    return url;
  }
}

class VideoComponent extends HTMLElement {
  #initialized = false;
  #disposed = false;
  #videoJsLoadStarted = false;
  #initializationPromise = null;
  #loadRetryTimer = null;
  #onSlideChangeListener = null;
  #handleIntersectBound = null;
  #observer = null;
  #inView = false;
  #playCause = null;

  constructor() {
    super();
    this.element = this;
    this.trueObserver = false;
  }

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;
    this.#disposed = false;

    this.options = this.#parseOptions();
    this.delegateElement = new Delegate(this.element);
    this.#handleIntersectBound = this.#handleIntersect.bind(this);

    if (this.options.using_fade === 'fade') {
      const slide = this.element.closest('.swiper-slide');
      if (
        Number(this.element.dataset.position) === 0 ||
        slide?.classList.contains('swiper-slide-active') ||
        slide?.classList.contains('swiper-slide-duplicate-active')
      ) {
        this.checkLoaded();
        this.#bindEvents();
      } else {
        this.#bindEvents();
      }
    } else if (this.hasAttribute('data-lazy-init')) {
      this.createObserver();
    } else {
      this.checkLoaded();
    }
  }

  disconnectedCallback() {
    this.#disposed = true;
    this.#initialized = false;

    window.removeEventListener('swiper:slide-changed', this.#onSlideChangeListener);
    this.#onSlideChangeListener = null;

    if (this.#loadRetryTimer !== null) {
      clearTimeout(this.#loadRetryTimer);
      this.#loadRetryTimer = null;
    }

    this.#observer?.disconnect();
    this.#observer = null;
    this.trueObserver = false;
    this.#inView = false;
    this.#playCause = null;
    this.#initializationPromise = null;

    this.delegateElement?.destroy?.();
    this.delegateElement = null;

    if (this.videoPlayer) {
      try {
        this.videoPlayer.dispose();
      } catch {
        // Player may already be disposed during section re-render.
      }
      this.videoPlayer = null;
    }
  }

  #parseOptions() {
    try {
      return JSON.parse(this.element.getAttribute('data-section-settings') || '{}');
    } catch {
      return {};
    }
  }

  #bindEvents() {
    window.removeEventListener('swiper:slide-changed', this.#onSlideChangeListener);
    this.#onSlideChangeListener = this.#onSlideChange.bind(this);
    window.addEventListener('swiper:slide-changed', this.#onSlideChangeListener);
  }

  async checkLoaded() {
    if (this.#disposed || this.videoPlayer) return;
    if (this.#initializationPromise) {
      return this.#initializationPromise;
    }

    this.#initializationPromise = this.#initializePlayer();

    try {
      await this.#initializationPromise;
    } finally {
      this.#initializationPromise = null;
    }
  }

  async #initializePlayer() {
    const ready = await this.#ensureVideoJs();
    if (this.#disposed || !ready) return;

    this.videoPlayerInitialization();
  }

  pause() {
    if (this.videoPlayer && !this.videoPlayer.paused()) {
      this.videoPlayer.pause();
    }
  }

  #loadVideoJsScript() {
    const url = window.theme?.videoScript;
    if (!url) return Promise.resolve();

    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      if (typeof videojs !== 'undefined') return Promise.resolve();

      return new Promise((resolve, reject) => {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('videojs load failed')), {
          once: true,
        });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('videojs load failed'));
      document.body.appendChild(script);
    });
  }

  async #ensureVideoJs() {
    if (typeof videojs !== 'undefined') return true;

    if (!this.#videoJsLoadStarted) {
      this.#videoJsLoadStarted = true;
      window.loadScript?.(window.theme?.videoScript);

      try {
        await this.#loadVideoJsScript();
      } catch {
        return false;
      }
    }

    for (let attempt = 0; attempt < VIDEOJS_LOAD_MAX_ATTEMPTS; attempt += 1) {
      if (this.#disposed) return false;
      if (typeof videojs !== 'undefined') return true;

      await new Promise((resolve) => {
        this.#loadRetryTimer = setTimeout(resolve, VIDEOJS_LOAD_RETRY_MS);
      });
      this.#loadRetryTimer = null;
    }

    return typeof videojs !== 'undefined';
  }

  #onSlideChange(event) {
    this.elementPosition = Number.parseInt(this.element.getAttribute('data-position'), 10);
    if (event.detail.currentIndex === this.elementPosition) {
      if (!this.element.getAttribute('loaded')) {
        const node = document.createElement('div');
        node.appendChild(
          this.element.querySelector('template').content.firstElementChild.cloneNode(true)
        );
        this.element.appendChild(node.firstElementChild).focus();
        this.element.setAttribute('loaded', 'true');
      }
      if (!this.videoPlayer) {
        this.checkLoaded();
      } else if (this.options.viewport) {
        this.#play('autoplay');
        this.createObserver();
      }
    } else if (this.videoPlayer && !this.videoPlayer.paused()) {
      this.videoPlayer.pause();
    }
  }

  #pickHostedVideoSource(media) {
    const sources = media?.sources;
    if (!sources?.length) return null;

    let topQuality = null;
    for (const source of sources) {
      if (source.format === 'mp4') {
        if (!topQuality || topQuality.height < source.height) {
          topQuality = source;
        }
      }
    }

    return topQuality ?? sources[0];
  }

  #resolveHostedVideoSrc(media) {
    const source = this.#pickHostedVideoSource(media);
    return source?.url ? `${source.url}#t=0.001` : '';
  }

  #resolveExternalVideoSrc(media) {
    if (!media) return { src: '', host: 'html5' };

    const host = media.host;
    if (host === 'youtube') {
      return {
        src: `https://www.youtube.com/watch?v=${media.external_id}`,
        host: 'youtube',
      };
    }

    if (host === 'vimeo') {
      return { src: media.external_id, host: 'vimeo' };
    }

    return { src: '', host: 'html5' };
  }

  #resolveLegacyVideoSrc(media) {
    if (typeof media !== 'string') return { src: '', host: 'html5' };

    if (media.includes('youtube')) {
      return { src: media, host: 'youtube' };
    }

    if (media.includes('vimeo')) {
      return { src: media, host: 'vimeo' };
    }

    return { src: media, host: 'html5' };
  }

  videoPlayerInitialization() {
    if (this.#disposed || this.videoPlayer) return;

    if (!this.element.getAttribute('loaded')) {
      const node = document.createElement('div');
      node.appendChild(
        this.element.querySelector('template').content.firstElementChild.cloneNode(true)
      );
      this.element.appendChild(node.firstElementChild).focus();
      this.element.setAttribute('loaded', 'true');
    }

    const isMobile = responsive.matchesBreakpoint('pocket');
    const isFullscreen = this.options.fullscreen === true;
    const useFill = isFullscreen || this.options.fill === true;

    let aspect_ratio = '';
    let src = '';
    let host = 'html5';

    if (isMobile) {
      if (!isFullscreen) aspect_ratio = this.options.aspect_mob || '9:16';

      if (this.options.videoType === 'video_inside_media') {
        src = this.#resolveHostedVideoSrc(this.options.videod);
        const aspectRatioHeight = Math.floor(100 / this.options.videod.aspect_ratio);
        if (!isFullscreen && !this.options.video_ratio) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
      } else if (this.options.videoType === 'video_inside_media_external') {
        const aspectRatioHeight = Math.floor(100 / this.options.videom.aspect_ratio);
        if (!isFullscreen && !this.options.video_ratio) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
        ({ src, host } = this.#resolveExternalVideoSrc(this.options.videom));
      } else {
        ({ src, host } = this.#resolveLegacyVideoSrc(this.options.videom));
      }
    } else {
      if (!isFullscreen) aspect_ratio = this.options.aspect_desk || '16:9';

      if (this.options.videoType === 'video_inside_media') {
        src = this.#resolveHostedVideoSrc(this.options.videod);
        const aspectRatioHeight = Math.floor(100 / this.options.videod.aspect_ratio);
        if (!isFullscreen && !this.options.video_ratio) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
      } else if (this.options.videoType === 'video_inside_media_external') {
        const aspectRatioHeight = Math.floor(100 / this.options.videod.aspect_ratio);
        if (!isFullscreen && !this.options.video_ratio) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
        ({ src, host } = this.#resolveExternalVideoSrc(this.options.videod));
      } else {
        ({ src, host } = this.#resolveLegacyVideoSrc(this.options.videod));
      }
    }

    const configured = Boolean(src);
    if (!configured) {
      src = 'https://www.youtube.com/watch?v=_9VUPq3SxOc';
      host = 'youtube';
    }

    const type =
      host === 'youtube' ? 'video/youtube' : host === 'vimeo' ? 'video/vimeo' : 'video/mp4';

    if (aspect_ratio) aspect_ratio = aspect_ratio.replace('/', ':');

    const payload = {
      autoplay: false,
      muted: this.options.muted,
      loop: this.options.loop,
      controls: false,
      poster: false,
      preload: 'metadata',
      playsinline: true,
      techOrder: [host],
      disablePictureInPicture: true,

      sources: [
        {
          type: type,
          src: src,
        },
      ],
    };

    // PDP gallery zoom: the video body opens the lightbox, so video.js must not
    // consume the click/tap itself (custom controls still handle play/seek/sound).
    if (this.options.openLightbox) {
      payload.userActions = { click: false, doubleClick: false };
    }

    if (useFill) {
      payload.fill = true;
      payload.responsive = false;
    } else {
      payload.aspectRatio = aspect_ratio;
      payload.responsive = true;
    }

    const videoplayerID = this.element.querySelector('video');
    this.videoPlayer = videojs(videoplayerID, payload);
    // `playing` rather than `firstplay`: it fires once playback really starts, after the
    // metadata, so the duration is known; `one` keeps it to the first start per player. The
    // bundled Vimeo tech never triggers `playing`, only `play` (its duration is known by then).
    // A block without a source plays the placeholder clip, which is not a video play.
    if (configured) {
      this.videoPlayer.one(host === 'vimeo' ? 'play' : 'playing', () =>
        this.#reportStart(host, src)
      );
    }

    this.videoPlayer.on('ready', () => {
      const holder = this.element.querySelector('.main-product__video-holder, .videoHolder');
      if (holder) holder.style.display = 'block';

      if (useFill) {
        const playerEl = this.videoPlayer.el();
        playerEl.classList.add('vjs-fill');
        playerEl.style.paddingTop = '0';
        playerEl.style.height = '100%';
      }

      if (this.trueObserver === false && this.options.viewport) {
        this.createObserver();
      } else if (this.#inView) {
        // Lazy-init players (promo tiles) are created from inside the observer callback,
        // which does not fire again until the element leaves and re-enters the viewport.
        this.#playInView();
      }

      this.bindCustomControls();
    });

    if (this.options.muted && host === 'vimeo') {
      this.videoPlayer.on('play', () => {
        this.videoPlayer.muted(true);
      });
    }

    // Body-tap toggles play ONLY when the video isn't a lightbox trigger. On PDP gallery
    // zoom (openLightbox), tapping the body must bubble up to open the lightbox instead;
    // play/seek/sound stay available through the custom control bar (bindCustomControls).
    if (this.options.controls && !this.options.openLightbox) {
      const togglePlay = (e) => {
        if (e?.target?.closest?.('.vjs-control-bar')) return;
        if (this.videoPlayer.paused()) {
          this.#play('user');
        } else {
          this.videoPlayer.pause();
        }
      };

      if (host === 'html5') {
        this.videoPlayer.on('touchstart', togglePlay);
        return;
      }
      this.videoPlayer.on('click', togglePlay);
    }
  }

  bindCustomControls() {
    this.stateElements = this.element.querySelectorAll("[data-action='toggle-state']");
    this.soundElements = this.element.querySelectorAll("[data-action='toggle-sound']");
    this.progressElements = this.element.querySelectorAll("[data-action='seek']");

    if (!this.stateElements.length || !this.soundElements.length) return;

    this.delegateElement.on('click', "[data-action='toggle-state']", (event) => {
      event.stopPropagation();
      this.videoPlayer.paused() ? this.#play('user') : this.videoPlayer.pause();
    });

    this.delegateElement.on('click', "[data-action='toggle-sound']", (event) => {
      event.stopPropagation();
      const shouldUnmute = this.videoPlayer.muted();
      if (shouldUnmute) {
        this.#muteSiblingPromoVideos();
      }
      this.videoPlayer.muted(!shouldUnmute);
    });

    this.delegateElement.on('click', "[data-action='seek']", (event, target) => {
      event.stopPropagation();
      const track = target.querySelector('.section-video__progress-track');
      if (!track) return;

      const duration = this.videoPlayer.duration();
      if (!duration || !Number.isFinite(duration)) return;

      const rect = track.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      this.videoPlayer.currentTime(duration * percent);
      this.updateProgress();
    });

    this.videoPlayer.on('play', () => {
      this.stateElements.forEach((el) => el.setAttribute('aria-label', 'playing'));
    });

    this.videoPlayer.on('pause', () => {
      this.stateElements.forEach((el) => el.setAttribute('aria-label', 'paused'));
    });

    this.videoPlayer.on('volumechange', () => {
      this.soundElements.forEach((el) =>
        el.setAttribute('aria-label', this.videoPlayer.muted() ? 'muted' : 'unmuted')
      );
    });

    this.videoPlayer.on('timeupdate', () => this.updateProgress());
    this.videoPlayer.on('loadedmetadata', () => this.updateProgress());

    this.stateElements.forEach((el) =>
      el.setAttribute('aria-label', this.videoPlayer.paused() ? 'paused' : 'playing')
    );
    this.soundElements.forEach((el) =>
      el.setAttribute('aria-label', this.videoPlayer.muted() ? 'muted' : 'unmuted')
    );
    this.updateProgress();
  }

  #muteSiblingPromoVideos() {
    const collectionGrid = this.closest('main-collection-grid');
    if (!collectionGrid) return;

    collectionGrid.querySelectorAll('video-component').forEach((component) => {
      if (component !== this && component.videoPlayer && !component.videoPlayer.muted()) {
        component.videoPlayer.muted(true);
      }
    });
  }

  updateProgress() {
    if (!this.progressElements?.length || !this.videoPlayer) return;

    const duration = this.videoPlayer.duration();
    const current = this.videoPlayer.currentTime();
    const percent = duration && Number.isFinite(duration) ? (current / duration) * 100 : 0;

    this.progressElements.forEach((progress) => {
      const playhead = progress.querySelector('.section-video__progress-playhead');
      if (playhead) playhead.style.setProperty('--video-progress', `${percent}%`);
    });
  }

  createObserver() {
    if (this.#observer) return;

    this.#observer = new IntersectionObserver(this.#handleIntersectBound, {
      threshold: 0.2,
      rootMargin: '200px 0px 200px 0px',
    });

    this.#observer.observe(this.element);
    this.trueObserver = true;
  }

  // `cause` tells the analytics hook whether the first play came from the viewport
  // observer or from the user; a play the browser blocks leaves the next one to the user.
  #play(cause) {
    // A play already under way keeps its cause (an observer callback must not relabel a user tap).
    if (!this.videoPlayer || !this.videoPlayer.paused()) return;

    this.#playCause = cause;
    const playResult = this.videoPlayer.play();
    if (playResult?.catch) {
      playResult.catch(() => {
        // Autoplay blocked — do not retry.
        this.#playCause = null;
      });
    }
  }

  #playInView() {
    if (!this.options.viewport || !this.videoPlayer) return;

    if (this.options.muted) {
      this.videoPlayer.muted(true);
    }
    this.#play('autoplay');
  }

  #reportStart(provider, src) {
    const key = `${window.location.pathname}#${this.options.id ?? src}`;
    if (reportedVideoStarts.has(key)) return;
    reportedVideoStarts.add(key);

    const fullUrl = provider === 'vimeo' && !src.includes('://') ? `https://vimeo.com/${src}` : src;
    const url = reportableUrl(fullUrl.split('#')[0]);
    const duration = this.videoPlayer.duration();
    const currentTime = this.videoPlayer.currentTime() || 0;
    const hasDuration = Number.isFinite(duration) && duration > 0;

    trackEvent(
      EVENTS.videoStart,
      {
        video_title: this.dataset.videoTitle?.trim() || videoTitleFromUrl(url),
        video_url: url,
        video_provider: provider,
        video_duration: hasDuration ? Math.round(duration) : undefined,
        video_current_time: Math.round(currentTime),
        video_percent: hasDuration ? Math.round((currentTime / duration) * 100) : undefined,
        video_autoplay: this.#playCause === 'autoplay',
        video_muted: this.videoPlayer.muted(),
        video_surface: this.dataset.videoSurface,
        product_id: this.dataset.productId,
        product_title: this.dataset.productTitle,
      },
      this
    );
  }

  #handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.intersectionRatio === 1 || entry.isIntersecting === true) {
        this.#inView = true;
        if (!this.videoPlayer) {
          this.checkLoaded();
          return;
        }

        this.#playInView();
      } else if (entry.intersectionRatio === 0 || entry.isIntersecting === false) {
        this.#inView = false;
        if (this.videoPlayer && !this.videoPlayer.paused()) {
          this.videoPlayer.pause();
        }
      }
    });
  }
}

if (!customElements.get('video-component')) {
  customElements.define('video-component', VideoComponent);
}
